import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  FolderOpen, FileText, Search, Download, Eye, File, FileSpreadsheet,
  Calendar, Tag, Plus, Loader2, Trash2, Building2, ChevronDown, ChevronUp,
  Edit, Euro, Clock, Receipt, LogIn, LogOut, Wallet, Mail, MessageCircle, ExternalLink, Share2,
  CreditCard, FileCheck, Send
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const iconForFormat = (format) => {
  switch (format) {
    case 'PDF': return <FileText size={20} color="#ef4444" />;
    case 'XLSX': return <FileSpreadsheet size={20} color="#22c55e" />;
    case 'DWG': return <File size={20} color="#3b82f6" />;
    default: return <File size={20} color="#6b7280" />;
  }
};

const Documenti = () => {
  const [documenti, setDocumenti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);
  const [acconti, setAcconti] = useState([]);
  const [timbrature, setTimbrature] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCantiere, setExpandedCantiere] = useState(null);
  const [showAddDoc, setShowAddDoc] = useState(null);
  const [newDoc, setNewDoc] = useState({ name: '', type: '', category: 'Generale' });
  const [docFile, setDocFile] = useState(null);
  // Acconti
  const [showAddAcconto, setShowAddAcconto] = useState(null);
  const [accontoData, setAccontoData] = useState({ workerId: '', amount: '', note: '', date: '' });
  const [editingAcconto, setEditingAcconto] = useState(null);
  const [editAccontoData, setEditAccontoData] = useState({ amount: '', note: '', date: '' });
  // Timbrature
  const [editingTimb, setEditingTimb] = useState(null);
  const [editTimbData, setEditTimbData] = useState({ date: '', time: '', type: '' });
  const [addingTimb, setAddingTimb] = useState(null);
  const [newTimbData, setNewTimbData] = useState({ workerId: '', date: '', time: '', type: 'ingresso' });
  // Pagamenti
  const [pagamenti, setPagamenti] = useState([]);
  const [showAddPagamento, setShowAddPagamento] = useState(null);
  const [pagamentoData, setPagamentoData] = useState({ amount: '', date: '', method: '', description: '', from_name: '' });
  const [editingPag, setEditingPag] = useState(null);
  const [editPagData, setEditPagData] = useState({});
  // Preventivi/Fatture
  const [prevFatture, setPrevFatture] = useState([]);
  const [showAddPF, setShowAddPF] = useState(null);
  const [pfData, setPfData] = useState({ type: 'preventivo', number: '', amount: '', date: '', description: '', client_name: '', status: 'emesso' });
  const [editingPF, setEditingPF] = useState(null);
  const [editPFData, setEditPFData] = useState({});
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const [d, c, o, a, t, pg, pf] = await Promise.all([
        api.getDocumenti(), api.getCantieri(), api.getOperai(),
        api.getAcconti(), api.getTimbrature(),
        api.getPagamenti(), api.getPrevFatture()
      ]);
      setDocumenti(d); setCantieri(c); setOperai(o); setAcconti(a); setTimbrature(t);
      setPagamenti(pg); setPrevFatture(pf);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const getDocsForCantiere = (cId) => documenti.filter(d => d.cantiereId === cId);
  const getGeneralDocs = () => documenti.filter(d => !d.cantiereId);
  const getAccontiForCantiere = (cId) => {
    const workerIds = operai.filter(o => o.cantiereId === cId).map(o => o.id);
    return acconti.filter(a => workerIds.includes(a.workerId));
  };
  const getTimbForCantiere = (cId) => timbrature.filter(t => t.cantiereId === cId);
  const getWorkersForCantiere = (cId) => operai.filter(o => o.cantiereId === cId);

  const totalAcconti = acconti.reduce((s, a) => s + a.amount, 0);

  // Doc CRUD
  const handleAddDoc = async (cantiereId) => {
    if (!newDoc.name || !newDoc.type) return;
    try {
      if (docFile) {
        await api.uploadDocumento(newDoc.name, newDoc.type, newDoc.category, cantiereId === 'generale' ? '' : cantiereId, docFile);
      } else {
        await api.createDocumento({ ...newDoc, cantiereId: cantiereId === 'generale' ? null : cantiereId });
      }
      setNewDoc({ name: '', type: '', category: 'Generale' });
      setDocFile(null);
      setShowAddDoc(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteDoc = async (id) => {
    try { await api.deleteDocumento(id); loadData(); } catch (e) { console.error(e); }
  };

  const getDocUrl = (doc) => doc.filename ? api.getUploadUrl(doc.filename) : null;

  const handleViewDoc = (doc) => {
    const url = getDocUrl(doc);
    if (url) window.open(url, '_blank');
  };

  const handleDownloadDoc = (doc) => {
    const url = getDocUrl(doc);
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name + '.' + (doc.format || 'pdf').toLowerCase();
      a.click();
    }
  };

  const handleShareEmail = (doc) => {
    const url = getDocUrl(doc);
    const subject = encodeURIComponent(`Documento: ${doc.name} — Colabella Group`);
    const body = encodeURIComponent(`Buongiorno,\n\nLe invio il documento "${doc.name}" (${doc.type}).\n\n${url ? 'Link: ' + url : ''}\n\nCordiali saluti,\nColabella Group`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    if (url) handleDownloadDoc(doc);
  };

  const handleShareWhatsApp = (doc) => {
    const url = getDocUrl(doc);
    const text = encodeURIComponent(`*Colabella Group*\nDocumento: ${doc.name}\nTipo: ${doc.type}\n${url ? 'Link: ' + url : ''}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // Acconti CRUD
  const handleAddAcconto = async () => {
    if (!accontoData.workerId || !accontoData.amount) return;
    try {
      await api.createAcconto({
        workerId: accontoData.workerId,
        amount: parseFloat(accontoData.amount),
        note: accontoData.note,
        date: accontoData.date || new Date().toISOString().split('T')[0],
      });
      setAccontoData({ workerId: '', amount: '', note: '', date: '' });
      setShowAddAcconto(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteAcconto = async (id) => {
    try { await api.deleteAcconto(id); loadData(); } catch (e) { console.error(e); }
  };

  const openEditAcconto = (a) => {
    setEditAccontoData({ amount: String(a.amount), note: a.note || '', date: a.date || '' });
    setEditingAcconto(a);
  };

  const handleSaveAcconto = async () => {
    if (!editingAcconto) return;
    try {
      await api.updateAcconto(editingAcconto.id, {
        amount: parseFloat(editAccontoData.amount), note: editAccontoData.note, date: editAccontoData.date,
      });
      setEditingAcconto(null); loadData();
    } catch (e) { console.error(e); }
  };

  // Timbrature edit
  const openEditTimb = (t) => {
    const dt = new Date(t.timestamp);
    setEditTimbData({ date: dt.toISOString().split('T')[0], time: dt.toTimeString().slice(0, 5), type: t.type });
    setEditingTimb(t);
  };

  const handleSaveTimb = async () => {
    if (!editingTimb || !editTimbData.date || !editTimbData.time) return;
    try {
      await api.updateTimbratura(editingTimb.id, { timestamp: `${editTimbData.date}T${editTimbData.time}:00`, type: editTimbData.type });
      setEditingTimb(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteTimb = async (id) => {
    try { await api.deleteTimbratura(id); loadData(); } catch (e) { console.error(e); }
  };

  const handleAddManualTimb = async () => {
    if (!newTimbData.workerId || !newTimbData.date || !newTimbData.time) return;
    try {
      await api.createTimbratura({ workerId: newTimbData.workerId, type: newTimbData.type, lat: 0, lng: 0 });
      const allT = await api.getTimbrature(newTimbData.date);
      const latest = allT.find(t => t.workerId === newTimbData.workerId && t.type === newTimbData.type);
      if (latest) await api.updateTimbratura(latest.id, { timestamp: `${newTimbData.date}T${newTimbData.time}:00` });
      setAddingTimb(null); setNewTimbData({ workerId: '', date: '', time: '', type: 'ingresso' }); loadData();
    } catch (e) { console.error(e); }
  };

  // Pagamenti CRUD
  const getPagForCantiere = (cId) => pagamenti.filter(p => p.cantiereId === cId);
  const handleAddPagamento = async (cantiereId) => {
    if (!pagamentoData.amount) return;
    try {
      await api.createPagamento({ ...pagamentoData, cantiereId, amount: parseFloat(pagamentoData.amount) });
      setPagamentoData({ amount: '', date: '', method: '', description: '', from_name: '' });
      setShowAddPagamento(null); loadData();
    } catch (e) { console.error(e); }
  };
  const handleDeletePag = async (id) => { try { await api.deletePagamento(id); loadData(); } catch (e) { console.error(e); } };
  const openEditPag = (p) => { setEditPagData({ amount: String(p.amount), date: p.date, method: p.method, description: p.description, from_name: p.from_name }); setEditingPag(p); };
  const handleSavePag = async () => {
    if (!editingPag) return;
    try { await api.updatePagamento(editingPag.id, { ...editPagData, amount: parseFloat(editPagData.amount) || 0 }); setEditingPag(null); loadData(); } catch (e) { console.error(e); }
  };

  // Preventivi/Fatture CRUD
  const getPFForCantiere = (cId) => prevFatture.filter(p => p.cantiereId === cId);
  const handleAddPF = async (cantiereId) => {
    if (!pfData.number && !pfData.description) return;
    try {
      await api.createPrevFattura({ ...pfData, cantiereId, amount: parseFloat(pfData.amount) || 0 });
      setPfData({ type: 'preventivo', number: '', amount: '', date: '', description: '', client_name: '', status: 'emesso' });
      setShowAddPF(null); loadData();
    } catch (e) { console.error(e); }
  };
  const handleDeletePF = async (id) => { try { await api.deletePrevFattura(id); loadData(); } catch (e) { console.error(e); } };
  const openEditPF = (p) => { setEditPFData({ type: p.type, number: p.number, amount: String(p.amount), date: p.date, description: p.description, client_name: p.client_name, status: p.status }); setEditingPF(p); };
  const handleSavePF = async () => {
    if (!editingPF) return;
    try { await api.updatePrevFattura(editingPF.id, { ...editPFData, amount: parseFloat(editPFData.amount) || 0 }); setEditingPF(null); loadData(); } catch (e) { console.error(e); }
  };

  // Share helpers
  const shareEmail = (subject, body) => { window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`); };
  const shareWhatsApp = (text) => { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`); };

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  const renderCantiereSection = (cantiere) => {
    const docs = getDocsForCantiere(cantiere.id);
    const cAcconti = getAccontiForCantiere(cantiere.id);
    const cTimb = getTimbForCantiere(cantiere.id);
    const cWorkers = getWorkersForCantiere(cantiere.id);
    const cAccontiTotal = cAcconti.reduce((s, a) => s + a.amount, 0);
    const isExpanded = expandedCantiere === cantiere.id;

    return (
      <div key={cantiere.id} className={`doc-cantiere-card ${isExpanded ? 'doc-cantiere-expanded' : ''}`}>
        <div className="doc-cantiere-header" onClick={() => setExpandedCantiere(isExpanded ? null : cantiere.id)}>
          <div className="doc-cantiere-icon"><Building2 size={20} /></div>
          <div className="doc-cantiere-info">
            <h3>{cantiere.name}</h3>
            <p>{cantiere.address}</p>
          </div>
          <div className="doc-cantiere-badges">
            <span className="dcb"><FileText size={13} /> {docs.length} documenti</span>
            <span className="dcb"><Euro size={13} /> €{cAccontiTotal.toFixed(2)} acconti</span>
            <span className="dcb"><Clock size={13} /> {cTimb.length} timbrature</span>
          </div>
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>

        {isExpanded && (
          <div className="doc-cantiere-body">
            {/* Documenti */}
            <div className="dcb-section">
              <div className="dcb-section-header">
                <h4><FileText size={15} /> Documenti ({docs.length})</h4>
                <button className="dcb-add-btn" onClick={() => { setShowAddDoc(cantiere.id); setNewDoc({ name: '', type: '', format: 'PDF', size: '', category: 'Generale' }); }}>
                  <Plus size={14} /> Aggiungi
                </button>
              </div>
              {docs.map(doc => (
                <div key={doc.id} className="dcb-doc-row">
                  {iconForFormat(doc.format)}
                  <span className="dcb-doc-name" onClick={() => handleViewDoc(doc)} style={{ cursor: doc.filename ? 'pointer' : 'default' }} title={doc.filename ? 'Clicca per aprire' : ''}>
                    {doc.name}
                  </span>
                  <span className="dcb-doc-type">{doc.type}</span>
                  <span className="dcb-doc-meta">{doc.format} {doc.size && `• ${doc.size}`}</span>
                  <span className="dcb-doc-date">{doc.date ? new Date(doc.date).toLocaleDateString('it-IT') : ''}</span>
                  <div className="doc-share-btns">
                    {doc.filename && <button className="doc-share-btn doc-btn-view" onClick={() => handleViewDoc(doc)} title="Apri"><Eye size={13} /></button>}
                    {doc.filename && <button className="doc-share-btn doc-btn-download" onClick={() => handleDownloadDoc(doc)} title="Scarica"><Download size={13} /></button>}
                    <button className="doc-share-btn doc-btn-email" onClick={() => handleShareEmail(doc)} title="Email"><Mail size={13} /></button>
                    <button className="doc-share-btn doc-btn-whatsapp" onClick={() => handleShareWhatsApp(doc)} title="WhatsApp"><MessageCircle size={13} /></button>
                    <button className="pa-delete" onClick={() => handleDeleteDoc(doc.id)} title="Elimina"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {docs.length === 0 && <p className="empty-text">Nessun documento</p>}
            </div>

            {/* Acconti */}
            <div className="dcb-section">
              <div className="dcb-section-header">
                <h4><Wallet size={15} /> Acconti Versati — Totale: <span style={{ color: '#f59e0b' }}>€{cAccontiTotal.toFixed(2)}</span></h4>
                <button className="dcb-add-btn" style={{ color: '#f59e0b', borderColor: '#fbbf24' }} onClick={() => { setShowAddAcconto(cantiere.id); setAccontoData({ workerId: '', amount: '', note: '', date: '' }); }}>
                  <Plus size={14} /> Acconto
                </button>
              </div>
              {cAcconti.map(a => (
                <div key={a.id} className="dcb-acconto-row">
                  <Receipt size={14} color="#f59e0b" />
                  <span className="dcb-a-name">{a.workerName}</span>
                  <span className="dcb-a-date">{new Date(a.date).toLocaleDateString('it-IT')}</span>
                  <span className="dcb-a-amount">€{a.amount.toFixed(2)}</span>
                  <span className="dcb-a-note">{a.note || '-'}</span>
                  <button className="pa-edit" onClick={() => openEditAcconto(a)}><Edit size={13} /></button>
                  <button className="pa-delete" onClick={() => handleDeleteAcconto(a.id)}><Trash2 size={13} /></button>
                </div>
              ))}
              {cAcconti.length === 0 && <p className="empty-text">Nessun acconto</p>}
            </div>

            {/* Timbrature */}
            <div className="dcb-section">
              <div className="dcb-section-header">
                <h4><Clock size={15} /> Timbrature Recenti ({cTimb.length})</h4>
                <button className="dcb-add-btn" style={{ color: '#3b82f6', borderColor: '#93c5fd' }} onClick={() => { setAddingTimb(cantiere.id); setNewTimbData({ workerId: '', date: new Date().toISOString().split('T')[0], time: '08:00', type: 'ingresso' }); }}>
                  <Plus size={14} /> Timbratura
                </button>
              </div>
              {cTimb.slice(0, 20).map(t => (
                <div key={t.id} className="dcb-timb-row">
                  <div className={`paga-timb-icon ${t.type === 'ingresso' ? 'type-in' : 'type-out'}`}>
                    {t.type === 'ingresso' ? <LogIn size={12} /> : <LogOut size={12} />}
                  </div>
                  <span className="dcb-t-name">{t.workerName}</span>
                  <span className="dcb-t-type">{t.type === 'ingresso' ? 'Ingresso' : 'Uscita'}</span>
                  <span className="dcb-t-date">{new Date(t.timestamp).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'numeric' })}</span>
                  <span className="dcb-t-time">{new Date(t.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                  <button className="pa-edit" onClick={() => openEditTimb(t)}><Edit size={13} /></button>
                  <button className="pa-delete" onClick={() => handleDeleteTimb(t.id)}><Trash2 size={13} /></button>
                </div>
              ))}
              {cTimb.length === 0 && <p className="empty-text">Nessuna timbratura</p>}
            </div>

            {/* Pagamenti Ricevuti */}
            <div className="dcb-section">
              <div className="dcb-section-header">
                <h4><CreditCard size={15} /> Pagamenti Ricevuti — Totale: <span style={{ color: '#22c55e' }}>€{getPagForCantiere(cantiere.id).reduce((s, p) => s + p.amount, 0).toFixed(2)}</span></h4>
                <button className="dcb-add-btn" style={{ color: '#22c55e', borderColor: '#86efac' }} onClick={() => { setShowAddPagamento(cantiere.id); setPagamentoData({ amount: '', date: new Date().toISOString().split('T')[0], method: '', description: '', from_name: '' }); }}>
                  <Plus size={14} /> Pagamento
                </button>
              </div>
              {getPagForCantiere(cantiere.id).map(p => (
                <div key={p.id} className="dcb-doc-row">
                  <CreditCard size={16} color="#22c55e" />
                  <span className="dcb-doc-name">{p.from_name || 'Pagamento'}</span>
                  <span className="dcb-a-amount" style={{ color: '#22c55e' }}>€{p.amount.toFixed(2)}</span>
                  <span className="dcb-a-date">{p.date ? new Date(p.date).toLocaleDateString('it-IT') : ''}</span>
                  <span className="dcb-doc-type">{p.method || '-'}</span>
                  <span className="dcb-a-note">{p.description || '-'}</span>
                  <div className="doc-share-btns">
                    <button className="doc-share-btn doc-btn-email" onClick={() => shareEmail(`Pagamento ricevuto — Colabella Group`, `Pagamento ricevuto:\nImporto: €${p.amount.toFixed(2)}\nDa: ${p.from_name}\nData: ${p.date}\nMetodo: ${p.method}\nDescrizione: ${p.description}\n\nColabella Group`)} title="Email"><Mail size={13} /></button>
                    <button className="doc-share-btn doc-btn-whatsapp" onClick={() => shareWhatsApp(`*Colabella Group — Pagamento Ricevuto*\nImporto: €${p.amount.toFixed(2)}\nDa: ${p.from_name}\nData: ${p.date}\nMetodo: ${p.method}`)} title="WhatsApp"><MessageCircle size={13} /></button>
                    <button className="pa-edit" onClick={() => openEditPag(p)}><Edit size={13} /></button>
                    <button className="pa-delete" onClick={() => handleDeletePag(p.id)}><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {getPagForCantiere(cantiere.id).length === 0 && <p className="empty-text">Nessun pagamento registrato</p>}
            </div>

            {/* Preventivi e Fatture */}
            <div className="dcb-section">
              <div className="dcb-section-header">
                <h4><FileCheck size={15} /> Preventivi Accettati e Fatture Emesse — Totale: <span style={{ color: '#3b82f6' }}>€{getPFForCantiere(cantiere.id).reduce((s, p) => s + p.amount, 0).toFixed(2)}</span></h4>
                <button className="dcb-add-btn" onClick={() => { setShowAddPF(cantiere.id); setPfData({ type: 'preventivo', number: '', amount: '', date: new Date().toISOString().split('T')[0], description: '', client_name: '', status: 'emesso' }); }}>
                  <Plus size={14} /> Nuovo
                </button>
              </div>
              {getPFForCantiere(cantiere.id).map(p => (
                <div key={p.id} className="dcb-doc-row">
                  <FileCheck size={16} color={p.type === 'fattura' ? '#f59e0b' : '#3b82f6'} />
                  <span className="dcb-doc-type" style={{ background: p.type === 'fattura' ? '#fef3c7' : '#eff6ff', color: p.type === 'fattura' ? '#d97706' : '#2563eb' }}>{p.type === 'fattura' ? 'FATTURA' : 'PREVENTIVO'}</span>
                  <span className="dcb-doc-name">{p.number ? `N. ${p.number}` : ''} {p.description}</span>
                  <span className="dcb-a-amount" style={{ color: '#3b82f6' }}>€{p.amount.toFixed(2)}</span>
                  <span className="dcb-a-date">{p.date ? new Date(p.date).toLocaleDateString('it-IT') : ''}</span>
                  <span className="dcb-doc-type" style={{ background: p.status === 'pagato' ? '#ecfdf5' : p.status === 'accettato' ? '#eff6ff' : p.status === 'rifiutato' ? '#fef2f2' : '#f3f4f6', color: p.status === 'pagato' ? '#16a34a' : p.status === 'accettato' ? '#2563eb' : p.status === 'rifiutato' ? '#dc2626' : '#6b7280' }}>{p.status.toUpperCase()}</span>
                  {p.client_name && <span className="dcb-a-note">{p.client_name}</span>}
                  <div className="doc-share-btns">
                    <button className="doc-share-btn doc-btn-email" onClick={() => shareEmail(`${p.type === 'fattura' ? 'Fattura' : 'Preventivo'} ${p.number} — Colabella Group`, `${p.type === 'fattura' ? 'Fattura' : 'Preventivo'} N. ${p.number}\nCliente: ${p.client_name}\nImporto: €${p.amount.toFixed(2)}\nData: ${p.date}\nDescrizione: ${p.description}\nStato: ${p.status}\n\nColabella Group`)} title="Email"><Mail size={13} /></button>
                    <button className="doc-share-btn doc-btn-whatsapp" onClick={() => shareWhatsApp(`*Colabella Group*\n${p.type === 'fattura' ? 'Fattura' : 'Preventivo'} N. ${p.number}\nCliente: ${p.client_name}\nImporto: €${p.amount.toFixed(2)}\nData: ${p.date}\nStato: ${p.status}`)} title="WhatsApp"><MessageCircle size={13} /></button>
                    <button className="pa-edit" onClick={() => openEditPF(p)}><Edit size={13} /></button>
                    <button className="pa-delete" onClick={() => handleDeletePF(p.id)}><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {getPFForCantiere(cantiere.id).length === 0 && <p className="empty-text">Nessun preventivo o fattura</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const generalDocs = getGeneralDocs();

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Gestione Documenti" subtitle="Documenti, acconti e timbrature per cantiere" />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Gestione Documenti</h1>
          <p className="page-subtitle">Documenti, acconti e timbrature per cantiere</p>
        </div>
        <div className="header-right">
          <SectionActions sectionTitle="Documenti" contentRef={pageRef} />
        </div>
      </div>

      {/* Summary */}
      <div className="doc-summary">
        <div className="doc-sum-card">
          <FileText size={20} color="#3b82f6" />
          <span className="doc-sum-val">{documenti.length}</span>
          <span className="doc-sum-lbl">Documenti Totali</span>
        </div>
        <div className="doc-sum-card">
          <Euro size={20} color="#f59e0b" />
          <span className="doc-sum-val">€{totalAcconti.toFixed(2)}</span>
          <span className="doc-sum-lbl">Acconti Totali Versati</span>
        </div>
        <div className="doc-sum-card">
          <Building2 size={20} color="#8b5cf6" />
          <span className="doc-sum-val">{cantieri.length}</span>
          <span className="doc-sum-lbl">Cantieri</span>
        </div>
        <div className="doc-sum-card">
          <Clock size={20} color="#22c55e" />
          <span className="doc-sum-val">{timbrature.length}</span>
          <span className="doc-sum-lbl">Timbrature</span>
        </div>
      </div>

      {/* Per-Cantiere Sections */}
      <div className="doc-cantiere-list">
        {cantieri.map(c => renderCantiereSection(c))}

        {/* General Documents */}
        <div className={`doc-cantiere-card ${expandedCantiere === 'generale' ? 'doc-cantiere-expanded' : ''}`}>
          <div className="doc-cantiere-header" onClick={() => setExpandedCantiere(expandedCantiere === 'generale' ? null : 'generale')}>
            <div className="doc-cantiere-icon" style={{ background: '#f3f4f6', color: '#6b7280' }}><FolderOpen size={20} /></div>
            <div className="doc-cantiere-info">
              <h3>Documenti Generali</h3>
              <p>Documenti non associati a un cantiere</p>
            </div>
            <div className="doc-cantiere-badges">
              <span className="dcb"><FileText size={13} /> {generalDocs.length} documenti</span>
            </div>
            {expandedCantiere === 'generale' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
          {expandedCantiere === 'generale' && (
            <div className="doc-cantiere-body">
              <div className="dcb-section">
                <div className="dcb-section-header">
                  <h4><FileText size={15} /> Documenti ({generalDocs.length})</h4>
                  <button className="dcb-add-btn" onClick={() => { setShowAddDoc('generale'); setNewDoc({ name: '', type: '', format: 'PDF', size: '', category: 'Generale' }); }}>
                    <Plus size={14} /> Aggiungi
                  </button>
                </div>
                {generalDocs.map(doc => (
                  <div key={doc.id} className="dcb-doc-row">
                    {iconForFormat(doc.format)}
                    <span className="dcb-doc-name" onClick={() => handleViewDoc(doc)} style={{ cursor: doc.filename ? 'pointer' : 'default' }}>
                      {doc.name}
                    </span>
                    <span className="dcb-doc-type">{doc.type}</span>
                    <span className="dcb-doc-meta">{doc.format} {doc.size && `• ${doc.size}`}</span>
                    <div className="doc-share-btns">
                      {doc.filename && <button className="doc-share-btn doc-btn-view" onClick={() => handleViewDoc(doc)} title="Apri"><Eye size={13} /></button>}
                      {doc.filename && <button className="doc-share-btn doc-btn-download" onClick={() => handleDownloadDoc(doc)} title="Scarica"><Download size={13} /></button>}
                      <button className="doc-share-btn doc-btn-email" onClick={() => handleShareEmail(doc)} title="Email"><Mail size={13} /></button>
                      <button className="doc-share-btn doc-btn-whatsapp" onClick={() => handleShareWhatsApp(doc)} title="WhatsApp"><MessageCircle size={13} /></button>
                      <button className="pa-delete" onClick={() => handleDeleteDoc(doc.id)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
                {generalDocs.length === 0 && <p className="empty-text">Nessun documento generale</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Document Dialog */}
      {showAddDoc && (
        <Dialog open={!!showAddDoc} onOpenChange={() => setShowAddDoc(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Aggiungi Documento</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Nome *</Label><Input value={newDoc.name} onChange={e => setNewDoc({...newDoc, name: e.target.value})} placeholder="Es: DVR Cantiere" /></div>
              <div className="form-field"><Label>Tipo *</Label><Input value={newDoc.type} onChange={e => setNewDoc({...newDoc, type: e.target.value})} placeholder="DVR, POS, DURC, Contratto..." /></div>
              <div className="form-field"><Label>Categoria</Label><Input value={newDoc.category} onChange={e => setNewDoc({...newDoc, category: e.target.value})} placeholder="Sicurezza, Progetto..." /></div>
              <div className="form-field"><Label>Carica File</Label>
                <input type="file" accept=".pdf,.xlsx,.xls,.doc,.docx,.dwg,.jpg,.png" onChange={e => setDocFile(e.target.files[0])} className="form-file-input" />
              </div>
              <button className="form-submit" onClick={() => handleAddDoc(showAddDoc)} disabled={!newDoc.name || !newDoc.type}>Aggiungi</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Acconto Dialog */}
      {showAddAcconto && (
        <Dialog open={!!showAddAcconto} onOpenChange={() => setShowAddAcconto(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo Acconto</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Operaio *</Label>
                <Select value={accontoData.workerId} onValueChange={v => setAccontoData({...accontoData, workerId: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona operaio" /></SelectTrigger>
                  <SelectContent>{getWorkersForCantiere(showAddAcconto).map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.badge})</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="form-field"><Label>Importo (€) *</Label><Input type="number" step="0.01" value={accontoData.amount} onChange={e => setAccontoData({...accontoData, amount: e.target.value})} placeholder="Es: 500.00" /></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={accontoData.date} onChange={e => setAccontoData({...accontoData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Note</Label><Input value={accontoData.note} onChange={e => setAccontoData({...accontoData, note: e.target.value})} placeholder="Es: Acconto marzo" /></div>
              <button className="form-submit" onClick={handleAddAcconto} disabled={!accontoData.workerId || !accontoData.amount}>Registra Acconto</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Acconto Dialog */}
      {editingAcconto && (
        <Dialog open={!!editingAcconto} onOpenChange={() => setEditingAcconto(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifica Acconto — {editingAcconto.workerName}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Importo (€)</Label><Input type="number" step="0.01" value={editAccontoData.amount} onChange={e => setEditAccontoData({...editAccontoData, amount: e.target.value})} /></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={editAccontoData.date} onChange={e => setEditAccontoData({...editAccontoData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Note</Label><Input value={editAccontoData.note} onChange={e => setEditAccontoData({...editAccontoData, note: e.target.value})} /></div>
              <button className="form-submit" onClick={handleSaveAcconto}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Timbratura Dialog */}
      {editingTimb && (
        <Dialog open={!!editingTimb} onOpenChange={() => setEditingTimb(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifica Timbratura — {editingTimb.workerName}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Tipo</Label>
                <Select value={editTimbData.type} onValueChange={v => setEditTimbData({...editTimbData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ingresso">Ingresso</SelectItem><SelectItem value="uscita">Uscita</SelectItem></SelectContent>
                </Select></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={editTimbData.date} onChange={e => setEditTimbData({...editTimbData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Ora</Label><Input type="time" value={editTimbData.time} onChange={e => setEditTimbData({...editTimbData, time: e.target.value})} /></div>
              <button className="form-submit" onClick={handleSaveTimb}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Manual Timbratura Dialog */}
      {addingTimb && (
        <Dialog open={!!addingTimb} onOpenChange={() => setAddingTimb(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Aggiungi Timbratura Manuale</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Operaio *</Label>
                <Select value={newTimbData.workerId} onValueChange={v => setNewTimbData({...newTimbData, workerId: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona operaio" /></SelectTrigger>
                  <SelectContent>{getWorkersForCantiere(addingTimb).map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="form-field"><Label>Tipo *</Label>
                <Select value={newTimbData.type} onValueChange={v => setNewTimbData({...newTimbData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ingresso">Ingresso</SelectItem><SelectItem value="uscita">Uscita</SelectItem></SelectContent>
                </Select></div>
              <div className="form-field"><Label>Data *</Label><Input type="date" value={newTimbData.date} onChange={e => setNewTimbData({...newTimbData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Ora *</Label><Input type="time" value={newTimbData.time} onChange={e => setNewTimbData({...newTimbData, time: e.target.value})} /></div>
              <button className="form-submit" onClick={handleAddManualTimb} disabled={!newTimbData.workerId || !newTimbData.date || !newTimbData.time}>Aggiungi</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Pagamento Dialog */}
      {showAddPagamento && (
        <Dialog open={!!showAddPagamento} onOpenChange={() => setShowAddPagamento(null)}><DialogContent>
          <DialogHeader><DialogTitle>Nuovo Pagamento Ricevuto</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-field"><Label>Importo (€) *</Label><Input type="number" step="0.01" value={pagamentoData.amount} onChange={e => setPagamentoData({...pagamentoData, amount: e.target.value})} placeholder="Es: 5000.00" /></div>
            <div className="form-field"><Label>Da (Pagante)</Label><Input value={pagamentoData.from_name} onChange={e => setPagamentoData({...pagamentoData, from_name: e.target.value})} placeholder="Nome committente" /></div>
            <div className="form-row">
              <div className="form-field"><Label>Data</Label><Input type="date" value={pagamentoData.date} onChange={e => setPagamentoData({...pagamentoData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Metodo</Label>
                <Select value={pagamentoData.method} onValueChange={v => setPagamentoData({...pagamentoData, method: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent><SelectItem value="bonifico">Bonifico</SelectItem><SelectItem value="contanti">Contanti</SelectItem><SelectItem value="assegno">Assegno</SelectItem><SelectItem value="carta">Carta</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="form-field"><Label>Descrizione</Label><Input value={pagamentoData.description} onChange={e => setPagamentoData({...pagamentoData, description: e.target.value})} placeholder="Es: SAL 1 lavori" /></div>
            <button className="form-submit" onClick={() => handleAddPagamento(showAddPagamento)} disabled={!pagamentoData.amount}>Registra Pagamento</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Edit Pagamento Dialog */}
      {editingPag && (
        <Dialog open={!!editingPag} onOpenChange={() => setEditingPag(null)}><DialogContent>
          <DialogHeader><DialogTitle>Modifica Pagamento</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-field"><Label>Importo (€)</Label><Input type="number" step="0.01" value={editPagData.amount} onChange={e => setEditPagData({...editPagData, amount: e.target.value})} /></div>
            <div className="form-field"><Label>Da</Label><Input value={editPagData.from_name || ''} onChange={e => setEditPagData({...editPagData, from_name: e.target.value})} /></div>
            <div className="form-row">
              <div className="form-field"><Label>Data</Label><Input type="date" value={editPagData.date || ''} onChange={e => setEditPagData({...editPagData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Metodo</Label><Input value={editPagData.method || ''} onChange={e => setEditPagData({...editPagData, method: e.target.value})} /></div>
            </div>
            <div className="form-field"><Label>Descrizione</Label><Input value={editPagData.description || ''} onChange={e => setEditPagData({...editPagData, description: e.target.value})} /></div>
            <button className="form-submit" onClick={handleSavePag}>Salva</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Add Preventivo/Fattura Dialog */}
      {showAddPF && (
        <Dialog open={!!showAddPF} onOpenChange={() => setShowAddPF(null)}><DialogContent>
          <DialogHeader><DialogTitle>Nuovo Preventivo / Fattura</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-row">
              <div className="form-field"><Label>Tipo *</Label>
                <Select value={pfData.type} onValueChange={v => setPfData({...pfData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="preventivo">Preventivo</SelectItem><SelectItem value="fattura">Fattura</SelectItem></SelectContent>
                </Select></div>
              <div className="form-field"><Label>Numero</Label><Input value={pfData.number} onChange={e => setPfData({...pfData, number: e.target.value})} placeholder="Es: 2025/001" /></div>
            </div>
            <div className="form-row">
              <div className="form-field"><Label>Importo (€)</Label><Input type="number" step="0.01" value={pfData.amount} onChange={e => setPfData({...pfData, amount: e.target.value})} /></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={pfData.date} onChange={e => setPfData({...pfData, date: e.target.value})} /></div>
            </div>
            <div className="form-field"><Label>Cliente</Label><Input value={pfData.client_name} onChange={e => setPfData({...pfData, client_name: e.target.value})} placeholder="Nome cliente" /></div>
            <div className="form-field"><Label>Descrizione</Label><Input value={pfData.description} onChange={e => setPfData({...pfData, description: e.target.value})} placeholder="Descrizione lavori" /></div>
            <div className="form-field"><Label>Stato</Label>
              <Select value={pfData.status} onValueChange={v => setPfData({...pfData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="emesso">Emesso</SelectItem><SelectItem value="accettato">Accettato</SelectItem><SelectItem value="pagato">Pagato</SelectItem><SelectItem value="rifiutato">Rifiutato</SelectItem></SelectContent>
              </Select></div>
            <button className="form-submit" onClick={() => handleAddPF(showAddPF)}>Crea</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Edit Preventivo/Fattura Dialog */}
      {editingPF && (
        <Dialog open={!!editingPF} onOpenChange={() => setEditingPF(null)}><DialogContent>
          <DialogHeader><DialogTitle>Modifica {editingPF.type === 'fattura' ? 'Fattura' : 'Preventivo'}</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-row">
              <div className="form-field"><Label>Tipo</Label>
                <Select value={editPFData.type || ''} onValueChange={v => setEditPFData({...editPFData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="preventivo">Preventivo</SelectItem><SelectItem value="fattura">Fattura</SelectItem></SelectContent>
                </Select></div>
              <div className="form-field"><Label>Numero</Label><Input value={editPFData.number || ''} onChange={e => setEditPFData({...editPFData, number: e.target.value})} /></div>
            </div>
            <div className="form-row">
              <div className="form-field"><Label>Importo (€)</Label><Input type="number" step="0.01" value={editPFData.amount || ''} onChange={e => setEditPFData({...editPFData, amount: e.target.value})} /></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={editPFData.date || ''} onChange={e => setEditPFData({...editPFData, date: e.target.value})} /></div>
            </div>
            <div className="form-field"><Label>Cliente</Label><Input value={editPFData.client_name || ''} onChange={e => setEditPFData({...editPFData, client_name: e.target.value})} /></div>
            <div className="form-field"><Label>Descrizione</Label><Input value={editPFData.description || ''} onChange={e => setEditPFData({...editPFData, description: e.target.value})} /></div>
            <div className="form-field"><Label>Stato</Label>
              <Select value={editPFData.status || ''} onValueChange={v => setEditPFData({...editPFData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="emesso">Emesso</SelectItem><SelectItem value="accettato">Accettato</SelectItem><SelectItem value="pagato">Pagato</SelectItem><SelectItem value="rifiutato">Rifiutato</SelectItem></SelectContent>
              </Select></div>
            <button className="form-submit" onClick={handleSavePF}>Salva</button>
          </div>
        </DialogContent></Dialog>
      )}
    </div>
  );
};

export default Documenti;
